import { Alert, AlertDescription, AlertIcon, AlertTitle, Box, Button, Flex } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  TextDocumentDto,
  useCreateSourcingEventTextDocumentMutation,
  useListTextDocumentsForSourcingEventQuery,
  ViewDocumentAsOwnerDto,
} from "../../../../../autogen/bff-api";
import { CombinedDocuments } from "../../../../../common/documents/CombinedDocuments";
import {
  IncompleteFileUpload,
  isIncompleteFileUpload,
  UploadCompletedStatus,
} from "../../../../../common/documents/UppyUploader/FileRow";
import { useUppyUploader } from "../../../../../common/documents/UppyUploader/useUppyUploader";
import { useApiError } from "../../../../../common/errors/useApiError";
import { useAppDispatch } from "../../../../../common/redux/hooks";
import { loadBseThunk } from "../../../../../common/redux/thunks/basic-sourcing-event/load-bse-thunk";
import { eventIsLive } from "../../../eventIsLive";
import { useBasicSourcingEventState } from "../../../useBasicSourcingEventState";

interface Props {
  nextStep: () => void;
  previousStep: () => void;
}

export const Docs = ({ nextStep, previousStep }: Props) => {
  const { t } = useTranslation();

  const eventState = useBasicSourcingEventState();
  const displayer = useApiError();
  const dispatch = useAppDispatch();

  const [createTextDocument, { isLoading: isCreatingTextDocument }] = useCreateSourcingEventTextDocumentMutation();
  const [textDocuments, setTextDocuments] = useState<TextDocumentDto[]>([]);

  const { data: textDocRes, isLoading: isLoadingTextDocuments } = useListTextDocumentsForSourcingEventQuery({
    eventId: eventState.id,
  });

  const loadEvent = useCallback(() => {
    dispatch(loadBseThunk({ eventId: eventState.id }));
  }, [dispatch, eventState.id]);

  const { uppy, allFiles, setDocuments, documents, setUploads, removeUpload } = useUppyUploader({
    entityId: eventState.id,
    entityType: "SourcingEvent",
    reloadEntity: loadEvent,
  });

  useEffect(() => {
    setDocuments(eventState.documents.value);
  }, [eventState.documents.value, setDocuments]);

  useEffect(() => {
    setTextDocuments(textDocRes?.documents ?? []);
  }, [textDocRes?.documents]);

  useEffect(() => {
    setUploads(eventState.uploads.value);
  }, [eventState.uploads.value, setUploads]);

  return (
    <Flex flexDirection="column" pt="10">
      <Box pb="8">
        {eventIsLive(eventState.event) && (
          <Alert status="info" borderRadius="lg">
            <AlertIcon />
            <AlertTitle>{t("You are live!")}</AlertTitle>
            <AlertDescription>
              {t("Files you upload will immediately be visible to invited suppliers")}
            </AlertDescription>
          </Alert>
        )}
      </Box>
      <CombinedDocuments
        uploadedDocuments={documents}
        setUploadedDocuments={setDocuments}
        textDocuments={textDocuments ?? []}
        setTextDocuments={setTextDocuments}
        isLoading={isLoadingTextDocuments}
        documentUploadConfig={{
          uppy,
          dropTargetId: "eventFileTarget",
          dropTargetHelperText: t("Upload documents describing the requirements of this sourcing event"),
          uploads: allFiles.filter((f) => isIncompleteFileUpload(f)) as IncompleteFileUpload[],
          removeUpload,
        }}
        createTextDocument={() =>
          createTextDocument({
            createSourcingEventTextDocumentRequest: {
              name: "",
              text: "",
              sourcingEventId: eventState.id,
              tags: [],
            },
          })
            .unwrap()
            .catch((e) => {
              displayer.show(e);
              throw e;
            })
        }
        isCreatingTextDocument={isCreatingTextDocument}
        canEditTextDocuments={true}
        canDeleteDocuments={true}
        canEditDocumentNames={true}
        isTagsColumnVisible={true}
        canEditTags={true}
        removeUploadedDocument={(document: ViewDocumentAsOwnerDto) =>
          removeUpload({
            status: UploadCompletedStatus.UploadCompleted,
            documentId: document.id,
            uploadId: document.fromUploadId,
            ...document,
          })
        }
      />
      <Flex pt="8" pb="10">
        <Button variant="outline" mr="5px" width="full" colorScheme="teal" onClick={previousStep}>
          {t("Previous")}
        </Button>
        <Button variant="solid" width="full" colorScheme="teal" onClick={nextStep}>
          {t("Next")}
        </Button>
      </Flex>
    </Flex>
  );
};
